import { useMemo } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useAgencyAvailability } from "../../../hooks/useAgencyAvailability";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { getAgencySubRoleInfo } from "../../../utils/agencySubRoles";
import { NotificationType, type NurseFullRole, type OpenShiftRequest, RequestResponsesStatusOptions, type ShiftRequest } from "../../../utils/models";
import { getRoleGroups } from "../../../utils/roles";
import { useGetDepartmentSubRoles } from "../../staffingSearch/useGetDepartmentSubRoles";

export interface RequestAgencyPostEligibility {
	/** Role-group names for the request (empty in job-management mode or when the dept has no role groups). */
	roleGroups: string[];
	/** True once the request already carries an agency_source_name. */
	isPostedToAgency: boolean;
	/** Open shift, still unfilled, not posted, agency available, AND job/role-groups map to a sub-role. */
	canNewlyPostToAgency: boolean;
}

/**
 * Eligibility for posting an existing open-shift request to an agency (Clipboard/Nursa). Centralizes the
 * gate shared by the Requests-tab button and any other surface that offers agency posting: a request is
 * postable only when its job (job-management ON) or role-groups (OFF) map to a sub-role in the
 * department's agency catalog for at least one available (configured AND active) agency.
 */
export const useCanPostRequestToAgency = (request: ShiftRequest): RequestAgencyPostEligibility => {
	const { chosenDepartment } = useHospitalData();
	const { availableAgencies } = useAgencyAvailability();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { data: subRoleCatalog = [] } = useGetDepartmentSubRoles(chosenDepartment.department_id);

	const roleGroups = useMemo(() => {
		if (!chosenDepartment.role_groups) return [];
		return getRoleGroups(request.message.roles as NurseFullRole[], chosenDepartment);
	}, [chosenDepartment, request.message.roles]);

	const isPostedToAgency = useMemo(() => {
		if (request.request_type !== NotificationType.OpenShift) return false;
		return !!(request as OpenShiftRequest).agency_source_name;
	}, [request]);

	const canNewlyPostToAgency = useMemo(() => {
		if (request.request_type !== NotificationType.OpenShift) return false;
		if (isPostedToAgency || availableAgencies.length === 0) return false;
		// Already waiting to be sent — offering "post to agency" again would be a no-op.
		if ((request as OpenShiftRequest).agency_post_delay_hours != null) return false;
		const neededNurses = (request as OpenShiftRequest).needed_nurses;
		const approvedCount = request.message.request_data.filter(({ status }) => status === RequestResponsesStatusOptions.APPROVED).length;
		if (approvedCount >= neededNurses) return false;
		return availableAgencies.some(
			(source) =>
				getAgencySubRoleInfo({
					catalog: subRoleCatalog,
					agencySource: source,
					jobManagementEnabled,
					roleGroups: chosenDepartment.role_groups,
					row: { jobId: request.job_id, roleGroups },
				}).options.length > 0,
		);
	}, [request, isPostedToAgency, availableAgencies, subRoleCatalog, jobManagementEnabled, chosenDepartment.role_groups, roleGroups]);

	return { roleGroups, isPostedToAgency, canNewlyPostToAgency };
};
